/**
 * Insured State Input Component
 *
 * Collects the state where insurance is registered.
 * Reused for primary, secondary, and retry insured state states.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "../insurance-connector-provider";
import {
  type InsuredStateFormData,
  insuredStateSchema,
} from "../machine/schemas/validation.schemas";
import { insuredStateData } from "@/components/InsuranceStepper/InsuranceStepper.constants";
import { INSURANCE_STEP_FORM_ID } from "../step-config";
import { StateAutocomplete } from "./StateAutocomplete";

export function InsuredStateInput() {
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const state = useInsuranceConnectorState();
  const t = useTranslations("InsuranceConnector");

  const stateValue = state.value as { full?: string } | string;
  const fullState = typeof stateValue === "object" ? stateValue.full : "";
  const isSecondary = fullState?.includes("secondary") ?? false;
  const isRetry = fullState?.includes("unverifiedRetry") ?? false;
  const insurance = isSecondary
    ? context.secondaryInsurance
    : context.primaryInsurance;

  const form = useForm<InsuredStateFormData>({
    resolver: zodResolver(insuredStateSchema),
    defaultValues: {
      insuredState: insurance?.insuredState || "",
    },
  });

  const onSubmit = (data: InsuredStateFormData) => {
    if (isSecondary) {
      send({
        type: "SUBMIT",
        data: { secondaryInsuredState: data.insuredState },
      });
    } else {
      send({ type: "SUBMIT", data });
    }
  };

  const headerText = isRetry
    ? t("ConfirmInsuredStateEdit")
    : isSecondary
      ? t("SecondaryInsuredStateQuestion")
      : t("PrimaryInsuredStateQuestion");

  const subheaderText = t("InsuredStateDifferentNote");

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl md:text-[32px] font-semibold flex flex-col md:space-y-0 md:leading-tight">
            {headerText}
          </h2>
          <p className="text-[20px] md:text-[32px] leading-[120%] tracking-[-0.02em] text-[#8A8787]">
            {subheaderText}
          </p>
        </div>
        <FormField
          control={form.control}
          name="insuredState"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="sr-only">{t("InsuredState")}</FormLabel>
              <FormControl>
                <StateAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  options={insuredStateData}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
